import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _decorate from "@babel/runtime/helpers/esm/decorate";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime/helpers/esm/get";
let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6,
  _t7;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _superPropGet(t, e, r, o) { var p = _get(_getPrototypeOf(1 & o ? t.prototype : t), e, r); return 2 & o ? function (t) { return p.apply(r, t); } : p; }
/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import on from '@carbon/web-components/es/globals/mixins/on.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import FocusMixin from '@carbon/web-components/es/globals/mixins/focus.js';
import "../expressive-modal/expressive-modal.js";
import "../expressive-modal/expressive-modal-close-button.js";
import "../lightbox-media-viewer/lightbox-image-viewer.js";
import "../button/button.js";
import { LIGHTBOX_CONTRAST } from "./defs.js";
import Maximize20 from '@carbon/web-components/es/icons/maximize/20.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from "././image.css.js";
import ModalRenderMixin from "../../globals/mixins/modal-render.js";
import StableSelectorMixin from "../../globals/mixins/stable-selector.js";
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
const {
  stablePrefix: c4dPrefix
} = settings;
export { LIGHTBOX_CONTRAST };

/**
 * Image.
 *
 * @element c4d-image
 * @slot long-description - The long description content.
 * @slot icon - The icon content.
 * @csspart image - The image. Usage: `c4d-image::part(image)`
 * @csspart picture - The picture tag. Usage:` c4d-image::part(picture)`
 * @csspart long-description - The wrapper around the long description slot. Usage: `c4d-image::part(long-description)`
 * @csspart lightbox-button - The button element for the lightbox when used. Usage: `c4d-image::part(lightbox-button)`
 * @csspart zoom-button - The wrapper around the zoom button to trigger the lightbox when used. Usage: `c4d-image::part(zoom-button)`
 * @csspart zoom-icon - The zoom icon used for the lightbox. Usage: `c4d-image::part(zoom-icon)`
 * @csspart caption - The caption for the image. Usage: `c4d-image::part(caption)`
 * @csspart expressive-modal - The expressive modal content. Usage: `c4d-image::part(expressive-modal)`
 * @csspart expressive-modal-close-button - The expressive modal content close button. Usage: `c4d-image::part(expressive-modal-close-button)`
 * @csspart lightbox-image-viewer - The lightbox image viewer component. Usage: `c4d-image::part(lightbox-image-viewer)`
 */
let C4DImage = _decorate([customElement(`${c4dPrefix}-image`)], function (_initialize, _StableSelectorMixin) {
  class C4DImage extends _StableSelectorMixin {
    constructor(...args) {
      super(...args);
      _initialize(this);
    }
  }
  return {
    F: C4DImage,
    d: [{
      kind: "field",
      decorators: [state()],
      key: "_images",
      value() {
        return [];
      }
    }, {
      kind: "method",
      key: "_handleSlotChange",
      value:
      /**
       * The image data, harvested from `<c4d-image-item>`.
       */

      /**
       * Handles `slotchange` event.
       */
      function _handleSlotChange({
        target
      }) {
        const {
          selectorItem
        } = this.constructor;
        this._images = target.assignedNodes()
        // Supports `<c4d-image><slot></slot></c4d-image>` rendered in shadow DOM
        .reduce((acc, node) => {
          if (node.tagName === 'SLOT') {
            acc.push(...node.assignedNodes());
          } else {
            acc.push(node);
          }
          return acc;
        }, []).filter(node => node.nodeType === Node.ELEMENT_NODE && node.matches(selectorItem));
      }

      /**
       * `true` handles re-opening after model is closed
       *
       * @private
       */
    }, {
      kind: "method",
      key: "_handleClick",
      value: function _handleClick() {
        this.open = true;
      }

      /**
       * The handler of `${c4dPrefix}-expressive-modal-closed` event from `<c4d-expressive-modal>`.
       */
    }, {
      kind: "field",
      key: "_handleCloseModal",
      value() {
        return () => {
          this.open = false;
        };
      }
    }, {
      kind: "field",
      key: "_hCloseModal",
      value() {
        return null;
      }
    }, {
      kind: "field",
      decorators: [property()],
      key: "alt",
      value() {
        return '';
      }
    }, {
      kind: "field",
      decorators: [property({
        attribute: 'default-src'
      })],
      key: "defaultSrc",
      value() {
        return '';
      }
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean,
        reflect: true
      })],
      key: "border",
      value() {
        return false;
      }
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean,
        reflect: true,
        attribute: 'card-group-item'
      })],
      key: "cardGroupItem",
      value() {
        return false;
      }
    }, {
      kind: "field",
      decorators: [property({
        attribute: 'lightbox-contrast'
      })],
      key: "lightboxContrast",
      value() {
        return LIGHTBOX_CONTRAST.LIGHT;
      }
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean,
        reflect: true
      })],
      key: "lightbox",
      value() {
        return false;
      }
    }, {
      kind: "field",
      decorators: [property()],
      key: "heading",
      value() {
        return '';
      }
    }, {
      kind: "field",
      decorators: [property({
        attribute: 'launch-lightbox-button-assistive-text'
      })],
      key: "launchLightboxButtonAssistiveText",
      value() {
        return 'launch light box media viewer';
      }
    }, {
      kind: "field",
      decorators: [property()],
      key: "copy",
      value() {
        return '';
      }
    }, {
      kind: "field",
      decorators: [property({
        type: Boolean,
        reflect: true
      })],
      key: "open",
      value() {
        return false;
      }
    }, {
      kind: "method",
      key: "connectedCallback",
      value:
      /**
       * The handle for the listener of `${c4dPrefix}-expressive-modal-closed` event.
       */
      /**
       * The alternate text.
       */
      /**
       * The image source.
       */
      /**
       * Whether or not to apply a border around the image.
       */
      /**
       * Whether or not it's a video thumbnail in a card group item.
       */
      /**
       * The lightbox contrast option.
       */
      /**
       * The lightbox.
       */
      /**
       * The heading.
       */
      /**
       * The description.
       */
      /**
       * `true` if the modal should be open.
       */
      function connectedCallback() {
        _superPropGet(C4DImage, "connectedCallback", this, 3)([]);
        this.modalRenderRoot = this.createModalRenderRoot(); // Creates modal render root up-front to hook the event listener
        // Manually hooks the event listeners on the modal render root to make the event names configurable
        this._hCloseModal = on(this.modalRenderRoot, this.constructor.eventCloseModal, this._handleCloseModal);
      }
    }, {
      kind: "method",
      key: "disconnectedCallback",
      value: function disconnectedCallback() {
        if (this._hCloseModal) {
          this._hCloseModal = this._hCloseModal.release();
        }
        _superPropGet(C4DImage, "disconnectedCallback", this, 3)([]);
      }
    }, {
      kind: "method",
      key: "renderImage",
      value: function renderImage() {
        const {
          alt,
          border,
          defaultSrc,
          lightbox,
          _images: images,
          _handleSlotChange: handleSlotChange
        } = this;
        const imgClasses = classMap({
          [`${c4dPrefix}--image__img`]: true,
          [`${c4dPrefix}--image__img--border`]: border && !lightbox
        });
        return html(_t || (_t = _`
      <slot @slotchange="${0}"></slot>
      <picture part="picture">
        ${0}
        <img
          class="${0}"
          src="${0}"
          alt="${0}"
          aria-describedby="image-caption long-description"
          part="image"
          loading="lazy" />
      </picture>
      <div
        id="long-description"
        class="${0}--image__longdescription"
        part="long-description">
        <slot name="long-description"></slot>
      </div>
      <slot name="icon"></slot>
    `), handleSlotChange, images.map(image => html(_t2 || (_t2 = _`<source media="${0}" srcset="${0}"></source>`), image.getAttribute('media'), image.getAttribute('srcset'))), imgClasses, defaultSrc, alt, c4dPrefix);
      }
    }, {
      kind: "method",
      key: "renderModal",
      value: function renderModal() {
        const {
          alt,
          copy,
          defaultSrc,
          heading,
          lightbox,
          open
        } = this;
        return !lightbox ? undefined : html(_t3 || (_t3 = _`
          <c4d-expressive-modal
            ?open="${0}"
            expressive-size="full-width"
            part="expressive-modal">
            <c4d-expressive-modal-close-button
              part="expressive-modal-close-button"></c4d-expressive-modal-close-button>
            <c4d-lightbox-image-viewer
              alt="${0}"
              default-src="${0}"
              description="${0}"
              title="${0}"
              part="lightbox-image-viewer">
            </c4d-lightbox-image-viewer>
          </c4d-expressive-modal>
        `), open, ifDefined(alt), ifDefined(defaultSrc), ifDefined(copy), ifDefined(heading));
      }
    }, {
      kind: "method",
      key: "render",
      value: function render() {
        const {
          heading,
          launchLightboxButtonAssistiveText,
          lightbox,
          _handleClick: handleClick
        } = this;
        return html(_t4 || (_t4 = _`
      ${0}
      ${0}
    `), lightbox ? html(_t5 || (_t5 = _`
            <button
              class="${0}--image-with-caption__image"
              aria-label="${0}"
              @click="${0}"
              part="lightbox-button">
              ${0}
              <div
                class="${0}--image-with-caption__zoom-button"
                part="zoom-button">
                ${0}
              </div>
            </button>
          `), c4dPrefix, ifDefined(launchLightboxButtonAssistiveText), handleClick, this.renderImage(), c4dPrefix, Maximize20({
          part: 'zoom-icon'
        })) : html(_t6 || (_t6 = _` ${0} `), this.renderImage()), heading ? html(_t7 || (_t7 = _`
            <p
              id="image-caption"
              class="${0}--image__caption"
              part="caption">
              ${0}
            </p>
          `), c4dPrefix, heading) : ``);
      }

      /**
       * The name of the custom event fired after the modal is closed upon a user gesture.
       */
    }, {
      kind: "get",
      static: true,
      key: "eventCloseModal",
      value: function () {
        return `${c4dPrefix}-expressive-modal-closed`;
      }

      /**
       * A selector that will return image items.
       */
    }, {
      kind: "get",
      static: true,
      key: "selectorItem",
      value: function () {
        return `${c4dPrefix}-image-item`;
      }
    }, {
      kind: "get",
      static: true,
      key: "stableSelector",
      value: function () {
        return `${c4dPrefix}--image`;
      }
    }, {
      kind: "field",
      static: true,
      key: "shadowRootOptions",
      value() {
        return _objectSpread(_objectSpread({}, LitElement.shadowRootOptions), {}, {
          delegatesFocus: true
        });
      }
    }, {
      kind: "field",
      static: true,
      key: "styles",
      value() {
        return styles;
      }
    }]
  };
}, StableSelectorMixin(ModalRenderMixin(FocusMixin(LitElement))));
/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DImage;
//# sourceMappingURL=image.js.map
